import { Card, CardContent, CardActions } from '@mui/material';
import { useState, useCallback } from 'react';
import { useTicketApproveMutation } from '../../../../services/ticketEasyService/ticketEasy-api';
import { TicketApprovalCommand } from '../../../../services/ticketEasyService/commands/approval/TicketApprovalCommand';
import { useMyProfileQuery } from '../../../../services/profileService/profile-api';
import { useAppSelector } from '../../../../app/hooks';
import { MessageDetailProps } from './types';
import { MessageHeader } from './MessageHeader';
import { MessageInfo } from './MessageInfo';
import { MessageDescription } from './MessageDescription';
import { MessageAttachments } from './MessageAttachments';
import { MessageActions } from './actions/MessageActions';
import { MessageNotifications } from './MessageNotifications';
import { RejectionComments } from '../rejections/RejectionComments';
import { MessageComments } from '../comments/MessageComments';

export function MessageDetail({ dataItem, onApprove, onReject, onReply, onFlag }: MessageDetailProps) {
    const { data: myProfile } = useMyProfileQuery();
    const [approving, setApproving] = useState(false);
    const [approveSuccess, setApproveSuccess] = useState(false);
    const [approveError, setApproveError] = useState<string | null>(null);
    const tickets = useAppSelector((state) => state.ticket.tickets);
    const [approveTicket] = useTicketApproveMutation();
    
    const handleApprove = useCallback(async () => {
        if (!dataItem.id) {
            console.error("Cannot approve message: Missing ticket ID");
            setApproveError("Cannot approve: Missing ticket ID");
            return;
        }
        
        setApproving(true);
        setApproveError(null);
        
        try {
            if (!myProfile) {
                console.error("Cannot approve message: Missing user data");
                setApproveError("Cannot approve: Missing user data");
                return;
            }
            const approvalCommand = new TicketApprovalCommand(dataItem.id, myProfile.identity, myProfile.email, dataItem.priority);
            await approveTicket(approvalCommand).unwrap();
            if (onApprove) onApprove();
            setApproveSuccess(true);
            setTimeout(() => {
                setApproveSuccess(false);
            }, 6000);
        } catch (error) {
            console.error("Failed to approve message:", error);
            setApproveError("Failed to approve message. Please try again.");
        } finally {
            setApproving(false);
        }
    }, [dataItem.id, myProfile, dataItem.priority, approveTicket, onApprove]);
    
    const closeSuccessNotification = useCallback(() => {
        setApproveSuccess(false);
    }, []);
    
    const closeErrorNotification = useCallback(() => {
        setApproveError(null);
    }, []);
    
    const handleReject = useCallback(() => {
        if (onReject) onReject();
    }, [onReject]);
    
    const handleReply = useCallback(() => {
        if (onReply) onReply();
    }, [onReply]);
    
    const handleFlag = useCallback(() => {
        if (onFlag) onFlag();
    }, [onFlag]);

    return (
        <Card sx={{ mb: 4, boxShadow: 2 }}>
            <MessageHeader dataItem={dataItem} />
            
            <CardContent>
                <MessageInfo dataItem={dataItem} />
                {dataItem.attachments && dataItem.attachments.length > 0 && (
                    <MessageAttachments attachments={dataItem.attachments} ticketId={dataItem.id} />
                )}
                <MessageDescription dataItem={dataItem} />
                
                {/* Display rejection comments if the ticket is rejected */}
                {dataItem.status === 'Rejected' && (
                    <RejectionComments approvals={dataItem.approvals} />
                )}
                
                {/* Display comments section */}
                <MessageComments 
                    comments={dataItem.comments || []} 
                    ticketId={dataItem.id || ''} 
                    currentUser={myProfile}
                />
            </CardContent>
            
            <CardActions sx={{ justifyContent: 'space-between', p: 2, bgcolor: 'background.paper' }}>
                <MessageActions 
                    dataItem={dataItem}
                    approving={approving}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onReply={handleReply}
                    onFlag={handleFlag}
                />
            </CardActions>
            
            <MessageNotifications
                approveSuccess={approveSuccess}
                approveError={approveError}
                onSuccessClose={closeSuccessNotification}
                onErrorClose={closeErrorNotification}
            />
        </Card>
    );
}